import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface ChannelROI {
  id: string
  channel: string
  investment: number
  revenue: number
  profit: number
  roi: number
  cac: number
  ltv: number
  ltv_cac_ratio: number
}

interface ROITrend {
  id: string
  period: string
  total_investment: number
  total_revenue: number
  roi: number
  efficiency_score: number
}

interface Statistics {
  total_investment: number
  total_revenue: number
  overall_roi: number
  avg_cac: number
  avg_ltv: number
  ltv_cac_ratio: number
}

export default function MarketingROIPage() {
  const [channels, setChannels] = useState<ChannelROI[]>([])
  const [trends, setTrends] = useState<ROITrend[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [channelsRes, trendsRes, statsRes] = await Promise.all([
        fetch('/api/marketingroi/channels'),
        fetch('/api/marketingroi/trends'),
        fetch('/api/marketingroi/statistics')
      ])
      if (channelsRes.ok) setChannels(await channelsRes.json())
      if (trendsRes.ok) setTrends(await trendsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching marketing ROI data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Marketing ROI</h1>
          <p className="mt-2 text-gray-600">Phase 249 - Return on marketing investment</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Investment</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.total_investment)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Revenue</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_revenue)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Overall ROI</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.overall_roi}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg CAC</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(statistics.avg_cac)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg LTV</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.avg_ltv)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">LTV:CAC</p>
              <p className="text-2xl font-bold text-green-600">{statistics.ltv_cac_ratio}x</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Channel ROI</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Channel</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Investment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ROI</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CAC</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">LTV</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">LTV:CAC</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {channels.map((channel) => (
                  <tr key={channel.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{channel.channel}</td>
                    <td className="px-6 py-4 text-gray-500">{formatCurrency(channel.investment)}</td>
                    <td className="px-6 py-4 text-green-600">{formatCurrency(channel.revenue)}</td>
                    <td className="px-6 py-4 text-purple-600 font-bold">{channel.roi}%</td>
                    <td className="px-6 py-4 text-orange-600">{formatCurrency(channel.cac)}</td>
                    <td className="px-6 py-4 text-blue-600">{formatCurrency(channel.ltv)}</td>
                    <td className={`px-6 py-4 font-bold ${channel.ltv_cac_ratio >= 15 ? 'text-green-600' : 'text-yellow-600'}`}>{channel.ltv_cac_ratio}x</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">ROI Trends</h2>
          </div>
          <div className="p-6 space-y-4">
            {trends.map((trend) => (
              <div key={trend.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{trend.period}</span>
                  <span className="text-lg font-bold text-purple-600">{trend.roi}% ROI</span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Investment</p>
                    <p className="font-medium">{formatCurrency(trend.total_investment)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Revenue</p>
                    <p className="font-medium text-green-600">{formatCurrency(trend.total_revenue)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Profit</p>
                    <p className="font-medium text-blue-600">{formatCurrency(trend.total_revenue - trend.total_investment)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Efficiency</p>
                    <p className="font-medium text-purple-600">{trend.efficiency_score}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
